import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';


// Import the modal components
import IssueForProductionViewModal from './IssueForProductionViewModal';
import ReceiptViewModal from './ReceiptViewModal';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const TrackDocumentIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const HidePanelIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

// Document type icons
const ProductionOrderIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const IssueIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const ReceiptIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const API_URL = '/api';


const ProductionOrderViewModal = ({ open, onClose, order }) => {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // ---- Department screen-config state ----
  const SCREEN_NAME = 'ProductionOrder';
  const [screenConfig, setScreenConfig] = useState(null);
  
  // Tracking states
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState(null);
  const [showTrackingPanel, setShowTrackingPanel] = useState(false);
  
  // State for document modals
  const [activeDocumentTab, setActiveDocumentTab] = useState(null);
  
  // State for issue and receipt modals
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedIssueForProduction, setSelectedIssueForProduction] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  
  // Toggle maximize/minimize function
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  const toggleTrackingPanel = () => {
    const newState = !showTrackingPanel;
    setShowTrackingPanel(newState);
    // Maximize when tracking panel is opened
    if (newState && !isMaximized) {
      setIsMaximized(true);
    }
  };
  
  useEffect(() => {
    if (open && order) {
      // Reset maximize state when modal opens
      setIsMaximized(false);
      setShowTrackingPanel(false);
      setActiveDocumentTab(null); // Reset document tab state
      
      // Reset modal states
      setViewModalOpen(false);
      setSelectedIssueForProduction(null);
      setSelectedReceipt(null);
      
      // Fetch screen configuration
      (async () => {
        try {
          const cfg = await fetchScreenConfig();
          setScreenConfig(cfg);
        } catch (e) {
          setScreenConfig(null);
        }
      })();
      
      // If we already have the order data, use it directly
      if (order && order.ProductionOrderID) {
        setOrderData(order);
        fetchTrackingData(order.ProductionOrderID);
        return;
      }
      
      // Otherwise, fetch the data using the order ID
      const fetchOrderData = async () => {
        try {
          setLoading(true);
          const response = await fetch(`${API_URL}/production-order/${order.ProductionOrderID || order}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });
          
          if (!response.ok) {
            throw new Error('Failed to fetch Production Order data');
          }
          
          const data = await response.json();
          setOrderData(data);
          fetchTrackingData(data.ProductionOrderID);
        } catch (error) {
          console.error('Error fetching Production Order data:', error);
          alert('Error loading Production Order data');
        } finally {
          setLoading(false);
        }
      };
      
      fetchOrderData();
    } else {
      // Reset data when modal is closed
      setOrderData(null);
      setTrackingData(null);
    }
  }, [open, order]);
  
  const fetchTrackingData = async (productionOrderId) => {
    setTrackingLoading(true);
    setTrackingError(null);
    try {
      const response = await axios.get(`${API_URL}/production-order-tracking/${productionOrderId}`);
      setTrackingData(response.data);
    } catch (error) {
      console.error('Error fetching tracking data:', error);
      setTrackingError('Failed to load tracking information');
    } finally {
      setTrackingLoading(false);
    }
  };
  
  const fetchScreenConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!res.ok) {
        let body = null;
        try { body = await res.json(); } catch (e) { /* ignore */ }
        const msg = body && body.message ? body.message : `Server returned ${res.status}`;
        throw new Error(msg);
      }

      const data = await res.json();
      return Array.isArray(data) ? (data[0] || null) : data;
    } catch (err) {
      console.error('fetchScreenConfig error:', err);
      throw err;
    }
  };
  
  // Helper truthy & show-checkers (Department feature)
  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };

  // Helper to determine if Department should show in header
  const shouldShowDepartmentOnHeader = (cfg) => {
    if (!cfg) return false;
    const showHeader = truthy(cfg.ShowOnHeader) || truthy(cfg.showOnHeader);
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    return showHeader && showScreen; // BOTH required
  };

  // Helper to determine if Department should show in items
  const shouldShowDepartmentOnItems = (cfg) => {
    if (!cfg) return false;
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    const showItem = truthy(cfg.ShowOnItem) || truthy(cfg.showOnItem);
    return showScreen && showItem; // BOTH required
  };
  
  // Enhanced Tree Structure Component for Production Order Tracking
  const TrackingTree = ({ data }) => {
    if (!data) return null;
    
    const { productionOrder, issues, receipts } = data;
    
    const StatusBadge = ({ status }) => {
      let statusStyle = 'bg-gray-100 text-gray-800';
      
      if (status === 'Completed') {
        statusStyle = 'bg-green-100 text-green-800';
      } else if (status === 'Fully Issued') {
        statusStyle = 'bg-blue-100 text-blue-800';
      } else if (status === 'Partially Issued') {
        statusStyle = 'bg-yellow-100 text-yellow-800';
      } else if (status === 'Partially Completed') {
        statusStyle = 'bg-orange-100 text-orange-800';
      } else if (status === 'Created') {
        statusStyle = 'bg-purple-100 text-purple-800';
      }
        
      return (
        <span className={`text-xs px-2 py-1 rounded-full ${statusStyle}`}>
          {status}
        </span>
      );
    };
    
    // Enhanced Document Node Component with click handling
    const DocumentNode = ({ 
      type, 
      number, 
      date, 
      status, 
      isPending = false,
      isCenter = false,
      onClick
    }) => {
      const getIcon = () => {
        switch(type) {
          case 'production':
            return <ProductionOrderIcon className="text-white" />;
          case 'issue':
            return <IssueIcon className={isPending ? "text-gray-400" : "text-white"} />;
          case 'receipt':
            return <ReceiptIcon className={isPending ? "text-gray-400" : "text-white"} />;
          default:
            return null;
        }
      };
      
      const getColorClass = () => {
        if (isPending) return 'bg-gray-200';
        switch(type) {
          case 'production':
            return 'bg-purple-500';
          case 'issue':
            return 'bg-blue-500';
          case 'receipt':
            return 'bg-green-500';
          default:
            return 'bg-gray-500';
        }
      };
      
      const isClickable = !isPending;
      
      const handleClick = () => {
        if (isClickable && onClick) {
          onClick(type, number);
        }
      };
      
      return (
        <div 
          className={`flex flex-col items-center mt-4 cursor-pointer hover:opacity-80 transition-opacity`}
          onClick={handleClick}
          title={isClickable ? `View ${type === 'production' ? 'Production Order' : type === 'issue' ? 'Issue' : 'Receipt'} in new tab` : 
                isPending ? 'Not created yet' : 'No permission to view'}
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getColorClass()}`}>
            {getIcon()}
          </div>
          <div className={`mt-2 text-center ${isPending ? 'text-gray-400' : 'text-gray-800'}`}>
            <div className="font-medium text-sm">
              {type === 'production' && 'Production Order'}
              {type === 'issue' && 'Issue'}
              {type === 'receipt' && 'Receipt'}
            </div>
            <div className="font-semibold">{number}</div>
            {date && !isPending && (
              <div className="text-xs text-gray-500 mt-1">
                {dayjs(date).format('MMM DD, YYYY')}
              </div>
            )}
            {status && !isPending && <StatusBadge status={status} className="mt-2" />}
            {isPending && (
              <div className="text-xs text-gray-400 italic mt-2">Not created yet</div>
            )}
          </div>
        </div>
      );
    };
    
    // Flowchart-style connector components
    const ProductionToIssueConnector = ({ count }) => {
      return (
        <div className="relative w-full h-16 flex items-center justify-center">
          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
            {/* Vertical line from Production Order */}
            <line 
              x1="50%" 
              y1="0" 
              x2="50%" 
              y2="25" 
              stroke="#4B5563" 
              strokeWidth="1"
            />
            
            {/* Horizontal connecting line */}
            <line 
              x1={`${100 / (count + 1)}%`} 
              y1="25" 
              x2={`${count * 100 / (count + 1)}%`} 
              y2="25" 
              stroke="#4B5563" 
              strokeWidth="1"
            />
            
            {/* Vertical lines to each Issue */}
            {[...Array(count)].map((_, i) => {
              const x = (i + 1) * (100 / (count + 1));
              return (
                <React.Fragment key={`issue-line-${i}`}>
                  <line 
                    x1={`${x}%`} 
                    y1="25" 
                    x2={`${x}%`} 
                    y2="70" 
                    stroke="#4B5563" 
                    strokeWidth="1"
                  />
                  {/* Arrowhead for each Issue */}
                  <polygon 
                    points={`${x},75 ${parseFloat(x) - 2},65 ${parseFloat(x) + 2},65`} 
                    fill="#4B5563" 
                  />
                </React.Fragment>
              );
            })}
          </svg>
        </div>
      );
    };
    
    const IssueToReceiptConnector = ({ issueCount, receiptCounts }) => {
      return (
        <div className="relative w-full h-16 flex items-center justify-center">
          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
            {/* Vertical lines from each issue */}
            {[...Array(issueCount)].map((_, i) => {
              const issueX = (i + 1) * (100 / (issueCount + 1));
              const receiptCount = receiptCounts[i] || 0;
              
              // Vertical line from issue
              return (
                <React.Fragment key={`issue-connector-${i}`}>
                  <line 
                    x1={`${issueX}%`} 
                    y1="0" 
                    x2={`${issueX}%`} 
                    y2="70" 
                    stroke="#4B5563" 
                    strokeWidth="1"
                  />
                  
                  {/* If there are receipts for this issue */}
                  {receiptCount > 0 && (
                    <>
                      {/* Horizontal line for receipts */}
                      <line 
                        x1={`${issueX - (receiptCount - 1) * 8 / 2}%`} 
                        y1="25" 
                        x2={`${issueX + (receiptCount - 1) * 8 / 2}%`} 
                        y2="25" 
                        stroke="#4B5563" 
                        strokeWidth="1"
                      />
                      
                      {/* Vertical lines to each receipt */}
                      {[...Array(receiptCount)].map((_, j) => {
                        const receiptX = issueX - (receiptCount - 1) * 8 / 2 + j * 8;
                        return (
                          <React.Fragment key={`receipt-line-${i}-${j}`}>
                            <line 
                              x1={`${receiptX}%`} 
                              y1="25" 
                              x2={`${receiptX}%`} 
                              y2="75" 
                              stroke="#4B5563" 
                              strokeWidth="1"
                            />
                            {/* Arrowhead for each receipt */}
                            <polygon 
                              points={`${receiptX},75 ${parseFloat(receiptX) - 2},65 ${parseFloat(receiptX) + 2},65`} 
                              fill="#4B5563" 
                            />
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
                  
                  {/* If no receipts for this issue, just add an arrow */}
                  {receiptCount === 0 && (
                    <polygon 
                      points={`${issueX},75 ${parseFloat(issueX) - 2},65 ${parseFloat(issueX) + 2},65`} 
                      fill="#4B5563" 
                    />
                  )}
                </React.Fragment>
              );
            })}
          </svg>
        </div>
      );
    };
    
    const DirectConnector = () => {
      return (
        <div className="relative w-full h-16 flex items-center justify-center">
          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
            <line 
              x1="50%" 
              y1="0" 
              x2="50%" 
              y2="75" 
              stroke="#4B5563" 
              strokeWidth="2"
            />
            <polygon 
              points="50,75 45,65 55,65" 
              fill="#4B5563" 
            />
          </svg>
        </div>
      );
    };
    
    // Handle document click
    const handleDocumentClick = (docType, docNumber) => {
      console.log(`Document clicked: ${docType}, Number: ${docNumber}`);
      if (docType === 'issue') {
        setSelectedIssueForProduction({ IssueID: docNumber });
        setSelectedReceipt(null);
        setViewModalOpen(true);
      } else if (docType === 'receipt') {
        setSelectedReceipt(docNumber);
        setSelectedIssueForProduction(null);
        setViewModalOpen(true);
      }
    };
    
    // Calculate receipt counts for each issue
    const getReceiptCounts = () => {
      if (!issues || !receipts) return [];
      
      return issues.map(issue => {
        return receipts.filter(receipt => 
          receipt.items.some(item => 
            item.productionOrderDetailID === issue.items[0]?.productionOrderDetailID
          )
        ).length;
      });
    };
    
    return (
      <div className="bg-white h-full">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-[#384959]">Document Tracking</h3>
          <button
            onClick={() => setShowTrackingPanel(false)}
            className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
            title="Hide Panel"
          >
            <HidePanelIcon size={20} />
          </button>
        </div>
        
        {trackingLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading tracking information...</p>
            </div>
          </div>
        ) : trackingError ? (
          <div className="bg-yellow-50 text-yellow-700 p-4 rounded-lg mb-6 border border-yellow-100">
            Tracking Error: {trackingError}
          </div>
        ) : data ? (
          <div className="flex flex-col items-center">
            {/* Production Order Section */}
            <DocumentNode 
              type="production"
              number={productionOrder.productionOrderID}
              date={productionOrder.CreatedAt}
              status={productionOrder.status}
              isCenter={true}
            />
            
            {/* Flowchart connector from Production Order to Issues */}
            {productionOrder.hasInputMaterials && issues && issues.length > 0 ? (
              <ProductionToIssueConnector count={issues.length} />
            ) : productionOrder.hasInputMaterials ? (
              <DirectConnector />
            ) : null}
            
            {/* Issues Section - only show if there are input materials */}
            {productionOrder.hasInputMaterials && (
              <>
                {issues && issues.length > 0 ? (
                  <div className="flex justify-center space-x-8 mt-2">
                    {issues.map(issue => (
                      <DocumentNode 
                        key={issue.issueID}
                        type="issue"
                        number={issue.issueID}
                        date={issue.issueDate}
                        status={issue.status}
                        onClick={handleDocumentClick}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex justify-center">
                    <DocumentNode 
                      type="issue"
                      number="Issue"
                      isPending={true}
                    />
                  </div>
                )}
              </>
            )}
            
            {/* Flowchart connector from Issues to Receipts */}
            {productionOrder.hasInputMaterials && issues && issues.length > 0 ? (
              <IssueToReceiptConnector 
                issueCount={issues.length} 
                receiptCounts={getReceiptCounts()} 
              />
            ) : !productionOrder.hasInputMaterials ? (
              <DirectConnector />
            ) : null}
            
            {/* Receipts Section */}
            {receipts && receipts.length > 0 ? (
              <div className="flex justify-center space-x-8 mt-2">
                {receipts.map(receipt => (
                  <DocumentNode 
                    key={receipt.receiptID}
                    type="receipt"
                    number={receipt.receiptID}
                    date={receipt.receiptDate}
                    status={receipt.status}
                    onClick={handleDocumentClick}
                  />
                ))}
              </div>
            ) : (
              <div className="flex justify-center">
                <DocumentNode 
                  type="receipt"
                  number="Receipt"
                  isPending={true}
                />
              </div>
            )}
            
            {/* Additional status information */}
            
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            No tracking data available
          </div>
        )}
      </div>
    );
  };
  
  if (!open) return null;
  
  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? "opacity-100 visible" : "opacity-0 invisible"}`}>
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className={`bg-white shadow-2xl flex flex-col transition-all duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} ${isMaximized ? 'w-full h-full max-w-none m-0 rounded-none' : showTrackingPanel ? 'w-[90%] max-w-7xl' : 'w-full max-w-6xl'} h-full rounded-none`}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">
              Production Order Details
            </h2>
            <div className="flex gap-2">
              <button 
                className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                  showTrackingPanel 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                }`}
                onClick={toggleTrackingPanel}
                title="Track Document"
              >
                <TrackDocumentIcon size={18} />
                <span>Track Document</span>
              </button>
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
              </button>
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1"
                onClick={onClose}
                title="Close"
              >
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Main Content Area with Sidebar Layout */}
        <div className="flex-grow overflow-hidden flex">
          {/* Main PO Details Content */}
          <div className={`flex-grow overflow-y-auto hide-scrollbar px-8 ${showTrackingPanel ? 'pr-4' : ''}`}>
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading production order details...</p>
                </div>
              </div>
            ) : (
              <div className="space-y-6 pb-6">
                {/* Production Order Header */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">Production Order Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">BOM Type *</label>
                      <input 
                        value={orderData?.BOMType || ''} 
                        disabled
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Order Number *</label>
                      <input 
                        value={orderData?.ProductionOrderID || ''} 
                        disabled
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Product *</label>
                      <input 
                        value={orderData?.ProductName || ''} 
                        disabled
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">BOM Header ID</label>
                      <input 
                        value={orderData?.BOMHeaderId || ''} 
                        disabled
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Quantity *</label>
                      <input 
                        value={orderData?.Qty ? parseFloat(orderData.Qty).toFixed(2) : ''} 
                        disabled
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">UOM *</label>
                      <input 
                        value={orderData?.UOM || ''} 
                        disabled
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Status *</label>
                      <input 
                        value={orderData?.Status || ''} 
                        disabled
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                      />
                    </div>
                    
                    {/* Department in Header (controlled by screen-config) */}
                    {shouldShowDepartmentOnHeader(screenConfig) && (
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Department</label>
                        <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                          {orderData?.DepartmentName
                            ? `${orderData.DepartmentCode ? orderData.DepartmentCode + ' - ' : ''}${orderData.DepartmentName}`
                            : (orderData?.DepartmentCode || orderData?.DepartmentID || '-')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Production Order Materials */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-[#384959]">Materials Required</h3>
                  </div>
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="min-w-full border border-[#E2E8F0]">
                      <thead>
                        <tr className="bg-[#BDDDFC] text-[#384959]">
                          <th className="p-2">#</th>
                          <th className="p-2">Material Type</th>
                          <th className="p-2">Product</th>
                          <th className="p-2">Product Code</th>
                          <th className="p-2">Product Name</th>
                          <th className="p-2">UOM</th>
                          <th className="p-2">BOM Qty</th>
                          <th className="p-2">Calculated Qty</th>
                          <th className="p-2">Expected Qty</th>
                          <th className="p-2">Issued/Received</th>
                          <th className="p-2">Flush Type</th>
                          {/* Department column - Only shown if ShowOnItem is true */}
                          {shouldShowDepartmentOnItems(screenConfig) && (
                            <th className="p-2">Department</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {orderData?.Details && orderData.Details.length > 0 ? (
                          orderData.Details.map((item, idx) => {
                            const expectedQty = item.materialType === 'Input' 
                              ? (item.CalculatedQty / item.Quantity) * orderData.Qty
                              : (item.CalculatedQty / item.Quantity) * orderData.Qty;
                              
                            const issuedReceivedQty = item.materialType === 'Input'
                              ? item.totalIssuedQty || 0
                              : item.totalReceivedQty || 0;
                              
                            return (
                              <tr key={idx} className="border-t border-[#E2E8F0]">
                                <td className="p-2 text-center">{idx + 1}</td>
                                <td className="p-2">
                                  <input 
                                    value={item.MaterialType || ''} 
                                    disabled
                                    className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]"
                                  />
                                </td>
                                <td className="p-2">
                                  <input 
                                    value={item.ProductName || ''} 
                                    disabled
                                    className="w-40 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]"
                                  />
                                </td>
                                <td className="p-2">
                                  <input 
                                    value={item.ProductCode || ''} 
                                    disabled
                                    className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]"
                                  />
                                </td>
                                <td className="p-2">
                                  <input 
                                    value={item.ProductName || ''} 
                                    disabled
                                    className="w-40 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]"
                                  />
                                </td>
                                <td className="p-2">
                                  <input 
                                    value={item.UOM || ''} 
                                    disabled
                                    className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]"
                                  />
                                </td>
                                <td className="p-2">
                                  <input 
                                    value={item.Qty ? parseFloat(item.Qty).toFixed(2) : ''} 
                                    disabled
                                    className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]"
                                  />
                                </td>
                                <td className="p-2">
                                  <input 
                                    value={item.CalculatedQty ? parseFloat(item.CalculatedQty).toFixed(2) : ''} 
                                    disabled
                                    className="w-20 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]"
                                  />
                                </td>
                                <td className="p-2">
                                  <input 
                                    value={expectedQty ? parseFloat(expectedQty).toFixed(2) : ''} 
                                    disabled
                                    className="w-20 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]"
                                  />
                                </td>
                                <td className="p-2">
                                  <input 
                                    value={issuedReceivedQty ? parseFloat(issuedReceivedQty).toFixed(2) : ''} 
                                    disabled
                                    className="w-20 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]"
                                  />
                                </td>
                                <td className="p-2">
                                  <input 
                                    value={item.FlushType || ''} 
                                    disabled
                                    className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]"
                                  />
                                </td>
                                
                                {/* Department cell - Only shown if ShowOnItem is true */}
                                {shouldShowDepartmentOnItems(screenConfig) && (
                                  <td className="p-2">
                                    <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                      {item.DepartmentCode || '-'}
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={shouldShowDepartmentOnItems(screenConfig) ? 12 : 11} className="p-4 text-center text-gray-500">
                              No materials found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Tracking Sidebar */}
          {showTrackingPanel && (
            <div className="border-l overflow-y-auto hide-scrollbar bg-white rounded-xl p-6 border border-gray-200 h-full" style={{width: "50vw"}}>
              <TrackingTree data={trackingData} />
            </div>
          )}
        </div>
        
        {/* Fixed Footer with Button */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button 
              type="button" 
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      
      {/* Document Modals */}
      {selectedIssueForProduction && (
        <IssueForProductionViewModal
          open={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false);
            setSelectedIssueForProduction(null);
          }}
          issueId={selectedIssueForProduction.IssueID}
        />
      )}
      
      {selectedReceipt && (
        <ReceiptViewModal
          open={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false);
            setSelectedReceipt(null);
          }}
          receiptId={selectedReceipt}
        />
      )}
    </div>
  );
};

export default ProductionOrderViewModal;